import { evaluate, toast } from "@kyan/ui";
import { useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { WITHDRAW_BUFFER } from "#/config/equity";
import { tokenAddresses, tokens } from "#/config/web3/tokens";
import { useWithdrawButtonFeatureFlag } from "#/hooks/featureFlags/useWithdrawButtonFeatureFlag";
import { useChainId } from "#/hooks/useChainId";
import { usePortfolioToastAction } from "#/hooks/usePortfolioToastActions";
import { useRefetchAccountStateIfStale } from "#/hooks/useRefetchAccountStateIfStale";
import { useUsdcBalance } from "#/hooks/useUsdcBalance";
import { useAccountHistory } from "#/services/kyan/hooks/useAccountHistory";
import { useWithdraw } from "#/services/kyan/hooks/useWithdraw";
import { useIsEquityModalEditing } from "#/store/app/equityModal/equityModalEditState";
import { useEquityModalPortfolio } from "#/store/app/equityModal/equityModalPortfolio";
import { useAccount } from "#/store/wallet/account/account";
import { useAccountState } from "#/store/wallet/account/accountState";
import { usePositions } from "#/store/wallet/account/positions";
import { useEoa } from "#/store/wallet/eoa/eoa";
import { type CompactAccountState } from "#/types/accounts";
import { PortfolioSymbol } from "#/types/markets";
import { getMarketByUnderlying } from "#/utils/marketPair";

import { useTxState } from "../../../../EquityModal.hook";
import { TxStatus } from "../../../../EquityModal.types";

const getMaxWithdrawable = (
  portfolioState: CompactAccountState,
  hasAnyPositions: boolean,
) => {
  if (!hasAnyPositions) {
    return portfolioState.equity;
  }
  const minRequiredEquity = portfolioState.im * (1 + WITHDRAW_BUFFER);
  return Number(
    Math.max(0, portfolioState.equity - minRequiredEquity).toFixed(
      tokens.USDC.decimals,
    ),
  );
};

export const useWithdrawFormController = () => {
  const { eoa } = useEoa();
  const { chainId } = useChainId();
  const { setTxState } = useTxState();
  const { account, setAccount } = useAccount();
  const { equityModalPortfolio, setEquityModalPortfolio } =
    useEquityModalPortfolio();
  const { setIsEquityModalEditing } = useIsEquityModalEditing();
  const { refetch: refetchAccountHistory } = useAccountHistory();
  const { refetch } = useRefetchAccountStateIfStale();
  const { getAction } = usePortfolioToastAction();

  const [amount, setAmount] = useState("");
  const [debouncedAmount] = useDebounceValue(amount, 600);

  const {
    usdcBalance,
    isLoading: isBalanceLoading,
    refetch: refetchUsdcBalance,
  } = useUsdcBalance();

  const { portfolioState } = useAccountState(equityModalPortfolio);
  const isUsdcPortfolio = equityModalPortfolio === PortfolioSymbol.USDC;
  const { positions } = usePositions(
    !isUsdcPortfolio ? getMarketByUnderlying(equityModalPortfolio) : undefined,
  );

  const maxWithdrawable = isUsdcPortfolio
    ? portfolioState.equity
    : getMaxWithdrawable(portfolioState, positions.length > 0);

  const {
    isWithdrawButtonEnabled,
    withdrawButtonTitle,
    withdrawButtonTooltip,
  } = useWithdrawButtonFeatureFlag();

  const isAmountValid = !isNaN(Number(amount)) && Number(amount) > 0;

  const ctaLabel =
    evaluate({
      "Enter amount": !isAmountValid,
    }) ?? withdrawButtonTitle;

  const errorMessage = evaluate({
    "Insufficient equity": Number(debouncedAmount) > maxWithdrawable,
  });

  const isDisabled =
    !isAmountValid || !isWithdrawButtonEnabled || Boolean(errorMessage);

  const { mutate: withdrawMutation, isPending: isLoading } = useWithdraw({
    onSuccess: () => {
      toast.success({
        title: "Withdraw Successful",
        action: getAction({
          closeModal: true,
          target: "history",
          label: "View in history",
        }),
      });
      refetchUsdcBalance();
      setTxState({
        status: TxStatus.SUCCESS,
        amount: Number(amount),
        toPortfolio: equityModalPortfolio,
      });
      refetchAccountHistory();
      refetch?.();
    },
    onError: (error) => {
      toast.error({ title: "Withdrawal failed", description: error.message });
      setTxState({ status: TxStatus.ERROR });
    },
    onPendingWithdraw: (signedWithdraw) => {
      setTxState({ status: TxStatus.PENDING, amount: signedWithdraw.amount });
    },
  });

  const handleWithdraw = () => {
    if (!eoa || !account) return;

    const withdrawTokenPair = {
      base: tokenAddresses[equityModalPortfolio][chainId],
      quote: tokenAddresses.USDC[chainId],
    };

    withdrawMutation({
      to: eoa,
      from: account,
      amount: Number(amount),
      pair: withdrawTokenPair,
    });
  };

  const setWithdrawAmount = (withdrawAmount: string) => {
    setAmount(withdrawAmount);
    setIsEquityModalEditing(Boolean(withdrawAmount));
  };

  return {
    handleWithdraw,
    ctaLabel,
    tooltip: withdrawButtonTooltip,
    isDisabled,
    isAmountValid,
    isLoading: isLoading || isBalanceLoading,
    usdcBalance,
    account,
    setAccount,
    amount,
    equityModalPortfolio,
    setEquityModalPortfolio,
    setWithdrawAmount,
    portfolioState,
    errorMessage,
    finalAmount: (portfolioState.equity - Number(amount)).toString(),
    maxWithdrawable,
  };
};
